import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { studioDomain } from '@/environment';
import Desktop from '@/images/Desktop.svg';
import Mobile from '@/images/Mobile.svg';
import { CampaignFormValues, PreviewType } from '@/pages/campaign/CreatePage/types';
import { CampaignCard } from '@/pages/campaign/LandingPage/components/CampaignCard';
import { useAuth } from '@/services/auth';
import theme from '@/theme';
import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import React, { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import DefaultCampaignLogo from '../../../../images/DefaultCampaignLogo.svg';
import { allowedColors } from '../SideBar/Fields/ColorsField';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

const ViewsButtons = styled.div`
    display: flex;
    flex-direction: row;
    gap: 16px;
    margin-block-start: 36px;
    margin-block-end: 32px;
    justify-content: center;
`;

const CampaignContainer = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
`;

const CenterContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    width: 100%;
`;

export const Preview = ({ isCreatingAgent }: { isCreatingAgent: boolean }) => {
    const [preview, setPreview] = React.useState<PreviewType>(PreviewType.DESKTOP);
    const { watch } = useFormContext<CampaignFormValues>();
    const { accessToken } = useAuth();
    const agent = watch('agent');
    const sendAnalytic = useCallback(() => {}, []);

    if (isCreatingAgent || !agent?.id) {
        return (
            <CenterContainer>
                <CircularProgress size="95px" />
            </CenterContainer>
        );
    }
    const [script, script_fields, logo_src, title, description, cta_enabled, cta_text, cta_url, cta_background_color] = watch([
        'script',
        'script_fields',
        'logo_src',
        'title',
        'description',
        'cta_enabled',
        'cta_text',
        'cta_url',
        'cta_background_color',
    ]);

    const inputFields = script_fields.reduce(
        (acc, field) => {
            acc[field] = 'preview';
            return acc;
        },
        {} as Record<string, string>
    );

    const logo_url = logo_src ? (logo_src instanceof File ? URL.createObjectURL(logo_src) : logo_src) : DefaultCampaignLogo;

    const campaign = {
        id: 'preview',
        agent_id: agent.id,
        duration: -1, // duration is not used in preview
        title,
        description,
        logo_url,
        cta_enabled,
        cta_text: cta_text || 'Button',
        cta_url: cta_url || studioDomain,
        cta_background_color: cta_background_color || allowedColors[0],
        script,
        script_fields,
    };

    return (
        <Container>
            <ViewsButtons>
                <IconButton onClick={() => setPreview(PreviewType.DESKTOP)} ariaLabel="Desktop Preview">
                    <MaskedIcon
                        src={Desktop}
                        size="32px"
                        color={preview === PreviewType.DESKTOP ? theme.colors.secondary.main : theme.colors.black[400]}
                        id="campaign-preview-desktop"
                    />
                </IconButton>
                <IconButton onClick={() => setPreview(PreviewType.MOBILE)} ariaLabel="Mobile Preview">
                    <MaskedIcon
                        src={Mobile}
                        size="32px"
                        color={preview === PreviewType.MOBILE ? theme.colors.secondary.main : theme.colors.black[400]}
                        id="campaign-preview-mobile"
                    />
                </IconButton>
            </ViewsButtons>

            <CampaignContainer id="campaign-preview-container">
                <CampaignCard
                    campaign={campaign}
                    inputFields={inputFields}
                    sendAnalytic={sendAnalytic}
                    auth={{ type: 'bearer', token: accessToken }}
                    preview={preview}
                />
            </CampaignContainer>
        </Container>
    );
};
